import React, { useState, useEffect, useCallback } from 'react';
import { Question, UserResponse, Subject, DiagnosticResult } from '../../types';
import { SmartHintService, SmartHintResult, HintLevel } from '../../services/smartHint';
import { ChatWidget } from '../ChatWidget/ChatWidget';
import { ChatContext } from '../../services/chatService';
import { Clock, CheckCircle, Lightbulb, X, Sparkles, Heart } from 'lucide-react';

interface TestEngineProps {
  questions: Question[];
  subject: Subject;
  onTestComplete: (result: DiagnosticResult, questions: Question[], responses: UserResponse[]) => void;
}

export const TestEngine: React.FC<TestEngineProps> = ({
  questions,
  subject,
  onTestComplete
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<UserResponse[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [timeSpent, setTimeSpent] = useState(0);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [isTestComplete, setIsTestComplete] = useState(false);
  
  // 智能提示相关状态
  const [smartHintService] = useState(() => new SmartHintService());
  const [currentHint, setCurrentHint] = useState<HintLevel | null>(null);
  const [hintLevel, setHintLevel] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isCheckingHint, setIsCheckingHint] = useState(false);
  const [lastHintCheck, setLastHintCheck] = useState(0);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  // 计时器
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeSpent(Date.now() - startTime);
    }, 100);

    return () => clearInterval(timer);
  }, [startTime]);

  // 重置状态当切换题目时
  useEffect(() => {
    setStartTime(Date.now());
    setTimeSpent(0);
    setCurrentHint(null);
    setHintLevel(0);
    setShowHint(false);
    setLastHintCheck(0);
  }, [currentQuestionIndex]);

  // 智能提示检查
  useEffect(() => {
    if (timeSpent - lastHintCheck > 3000) { // 每3秒检查一次
      setLastHintCheck(timeSpent);
      checkForSmartHint();
    }
  }, [timeSpent]);

  const checkForSmartHint = async () => {
    if (selectedAnswer || isCheckingHint || isTestComplete) return;

    setIsCheckingHint(true);
    try {
      const hintResult: SmartHintResult = await smartHintService.analyzeNeedForHint(
        currentQuestion,
        timeSpent,
        !!selectedAnswer,
        hintLevel
      );

      if (hintResult.shouldShowHint && hintResult.hint) {
        setCurrentHint(hintResult.hint);
        setHintLevel(hintResult.hint.level);
        // 不自动显示，让用户主动点击
      }
    } catch (error) {
      console.error('智能提示检查失败:', error);
    } finally {
      setIsCheckingHint(false);
    }
  };

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
    // 选择答案后隐藏提示
    setShowHint(false);
  };

  const handleNextQuestion = () => {
    if (!selectedAnswer) return;

    const isCorrect = selectedAnswer === currentQuestion.content.correct_answer.toString();
    const response: UserResponse = {
      question_id: currentQuestion.id,
      answer: selectedAnswer,
      time_spent: timeSpent,
      is_correct: isCorrect
    };

    const newResponses = [...responses, response];
    setResponses(newResponses);
    setSelectedAnswer('');

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // 测试完成，计算结果
      const result = calculateDiagnosticResult(newResponses, questions);
      setIsTestComplete(true);
      onTestComplete(result, questions, newResponses);
    }
  };

  const toggleHint = () => {
    setShowHint(!showHint);
  };

  const calculateDiagnosticResult = (
    responses: UserResponse[],
    questions: Question[]
  ): DiagnosticResult => {
    // 简化版诊断算法
    const dimensionScores = {
      processing_speed: 0,
      working_memory: 0,
      abstract_thinking: 0,
      pattern_recognition: 0,
      transfer_application: 0,
      metacognitive_monitoring: 0
    };

    const dimensionCounts = { ...dimensionScores };

    questions.forEach((question, index) => {
      const response = responses[index];
      if (response) {
        const dimension = question.skill_dimension;
        
        // 基础分数（正确性）
        let score = response.is_correct ? 0.7 : 0.3;
        
        // 速度分数（基于时间限制）
        if (question.time_limit && response.time_spent < question.time_limit * 1000) {
          const speedRatio = 1 - (response.time_spent / (question.time_limit * 1000));
          score += speedRatio * 0.3;
        }
        
        dimensionScores[dimension] += Math.min(score, 1);
        dimensionCounts[dimension] += 1;
      }
    });

    // 计算平均分
    Object.keys(dimensionScores).forEach(key => {
      const dimension = key as keyof typeof dimensionScores;
      if (dimensionCounts[dimension] > 0) {
        dimensionScores[dimension] = dimensionScores[dimension] / dimensionCounts[dimension];
      }
    });

    // 找出弱项
    const weaknessThreshold = 0.6;
    const weaknessAreas = Object.entries(dimensionScores)
      .filter(([_, score]) => score < weaknessThreshold)
      .map(([dimension, _]) => getDimensionName(dimension));

    // 生成建议
    const recommendations = generateRecommendations(dimensionScores);

    // 计算总分
    const overallScore = Object.values(dimensionScores).reduce((a, b) => a + b, 0) / 6;

    return {
      ability_scores: dimensionScores,
      weakness_areas: weaknessAreas,
      learning_recommendations: recommendations,
      overall_score: overallScore
    };
  };

  const getDimensionName = (dimension: string): string => {
    const names = {
      processing_speed: '信息处理速度',
      working_memory: '工作记忆',
      abstract_thinking: '抽象思维',
      pattern_recognition: '模式识别',
      transfer_application: '迁移应用',
      metacognitive_monitoring: '元认知监控'
    };
    return names[dimension as keyof typeof names] || dimension;
  };

  const generateRecommendations = (scores: any): string[] => {
    const recommendations = [];
    
    if (scores.processing_speed < 0.6) {
      recommendations.push('建议多做限时练习来提高处理速度');
    }
    if (scores.working_memory < 0.6) {
      recommendations.push('建议练习多步骤计算来增强工作记忆');
    }
    if (scores.abstract_thinking < 0.6) {
      recommendations.push('建议多做逻辑推理题来培养抽象思维');
    }
    if (scores.pattern_recognition < 0.6) {
      recommendations.push('建议练习找规律题型来提高模式识别能力');
    }
    if (scores.transfer_application < 0.6) {
      recommendations.push('建议多做应用题来提高知识迁移能力');
    }
    
    return recommendations.length > 0 ? recommendations : ['各项能力均衡，继续保持！'];
  };

  if (isTestComplete) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">测试完成！</h2>
          <p className="text-gray-600">AI正在深度分析您的答题情况...</p>
        </div>
      </div>
    );
  }

  const timeInSeconds = Math.round(timeSpent / 1000);
  const timeLimit = currentQuestion.time_limit;
  const isTimeWarning = timeLimit && timeInSeconds > timeLimit * 0.7;

  // 构建聊天上下文
  const chatContext: ChatContext = {
    currentQuestion,
    previousQuestions: questions.slice(0, currentQuestionIndex),
    userResponses: responses,
    subject,
    timeSpentOnCurrentQuestion: timeSpent,
    userStrengths: [], // 可以基于responses分析得出
    userWeaknesses: [] // 可以基于responses分析得出
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* AI聊天伴侣 */}
      <ChatWidget chatContext={chatContext} />

      {/* 进度条 */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">
            题目 {currentQuestionIndex + 1} / {questions.length}
          </span>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center text-sm ${isTimeWarning ? 'text-red-600' : 'text-gray-600'}`}>
              <Clock className="h-4 w-4 mr-1" />
              {timeInSeconds}秒
              {timeLimit && (
                <span className="ml-1 text-xs">
                  / {timeLimit}秒
                </span>
              )}
            </div>
            
            {/* 智能提示按钮 */}
            {currentHint && (
              <div className="relative">
                <button
                  onClick={toggleHint}
                  className={`flex items-center px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    showHint 
                      ? 'bg-yellow-500 text-white' 
                      : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 animate-pulse'
                  }`}
                >
                  <Lightbulb className="h-3 w-3 mr-1" />
                  AI助手 ({currentHint.level}级提示)
                </button>
                
                {!showHint && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* 智能提示卡片 */}
      {currentHint && showHint && (
        <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-lg p-4 shadow-md">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <Sparkles className="h-5 w-5 text-yellow-600 mr-2" />
                <h4 className="font-semibold text-yellow-800">{currentHint.title}</h4>
                <span className="ml-2 px-2 py-1 bg-yellow-200 text-yellow-700 text-xs rounded-full">
                  第{currentHint.level}级提示
                </span>
              </div>
              <p className="text-yellow-700 mb-2 leading-relaxed">{currentHint.content}</p>
              {currentHint.encouragement && (
                <div className="flex items-center text-sm text-yellow-600">
                  <Heart className="h-4 w-4 mr-1 text-red-500" />
                  {currentHint.encouragement}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowHint(false)}
              className="ml-4 p-1 text-yellow-600 hover:text-yellow-800 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* 题目内容 */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="mb-4">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mb-2">
            {subject === 'math' ? '数学' : '语文'} - 难度 {currentQuestion.difficulty}
          </span>
          {currentQuestion.time_limit && (
            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full mb-2 ml-2 ${
              isTimeWarning ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              限时 {currentQuestion.time_limit}秒
            </span>
          )}
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 mb-6 leading-relaxed">
          {currentQuestion.content.title}
        </h2>

        {/* 选项 */}
        <div className="space-y-3">
          {currentQuestion.content.options?.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(option)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedAnswer === option
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="font-medium mr-3">
                {String.fromCharCode(65 + index)}.
              </span>
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* 底部信息和按钮 */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">
          {isCheckingHint && (
            <div className="flex items-center">
              <Sparkles className="h-4 w-4 mr-1 animate-spin" />
              AI助手正在分析...
            </div>
          )}
        </div>
        
        <button
          onClick={handleNextQuestion}
          disabled={!selectedAnswer}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            selectedAnswer
              ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {currentQuestionIndex < questions.length - 1 ? '下一题' : '完成测试'}
        </button>
      </div>
    </div>
  );
};